import { randomUUID } from 'node:crypto';
import {
  createEmptyConversationGraph,
  isSessionConversationGraphAwareSession,
  type Session,
  type SessionConversationGraphSnapshot,
  type SessionConversationItemMeta,
  type SessionConversationTurn,
  type SessionItemOrigin,
  type SessionTurnStatus,
} from '../memory/session';
import type { RunState } from '../run-state';
import type { AgentInputItem } from '../types';

export type SessionTurnContext = {
  branchId: string;
  parentTurnId?: string;
  userTurnId: string;
  assistantTurnId: string;
  createdAt: string;
};

export async function createSessionTurnContext(options: {
  session?: Session;
  userItems?: AgentInputItem[];
}): Promise<SessionTurnContext | undefined> {
  const { session, userItems = [] } = options;
  if (
    !isSessionConversationGraphAwareSession(session) ||
    userItems.length === 0
  ) {
    return undefined;
  }
  const graph =
    (await session.getConversationGraph()) ?? createEmptyConversationGraph();
  const branchId = graph.activeBranchId || 'main';
  const createdAt = new Date().toISOString();
  return {
    branchId,
    parentTurnId: findLatestTurnId(graph, branchId),
    userTurnId: randomUUID(),
    assistantTurnId: randomUUID(),
    createdAt,
  };
}

export async function recordSessionInputTurn(options: {
  session?: Session;
  turnContext?: SessionTurnContext;
  userItems: AgentInputItem[];
}): Promise<void> {
  const { session, turnContext, userItems } = options;
  if (!turnContext || !isSessionConversationGraphAwareSession(session)) {
    return;
  }
  const now = new Date().toISOString();
  const userItemIds = collectItemIds(userItems);
  await session.updateConversationGraph({
    activeBranchId: turnContext.branchId,
    turns: [
      buildTurn({
        id: turnContext.userTurnId,
        role: 'user',
        branchId: turnContext.branchId,
        parentTurnId: turnContext.parentTurnId,
        itemIds: userItemIds,
        status: 'completed',
        createdAt: turnContext.createdAt,
        updatedAt: now,
      }),
      buildTurn({
        id: turnContext.assistantTurnId,
        role: 'assistant',
        branchId: turnContext.branchId,
        parentTurnId: turnContext.userTurnId,
        itemIds: [],
        status: 'in_progress',
        createdAt: turnContext.createdAt,
        updatedAt: now,
      }),
    ],
    itemMeta: userItems.flatMap((item) =>
      buildItemMeta(item, {
        turnId: turnContext.userTurnId,
        branchId: turnContext.branchId,
      }),
    ),
  });
}

export async function recordSessionFullTurn(options: {
  session?: Session;
  turnContext?: SessionTurnContext;
  userItems: AgentInputItem[];
  assistantItems: AgentInputItem[];
  assistantStatus: SessionTurnStatus;
}): Promise<void> {
  const { session, turnContext, userItems, assistantItems, assistantStatus } =
    options;
  if (!turnContext || !isSessionConversationGraphAwareSession(session)) {
    return;
  }
  const now = new Date().toISOString();
  const userItemIds = collectItemIds(userItems);
  const assistantItemIds = collectItemIds(assistantItems);
  const turns: SessionConversationTurn[] = [];
  if (userItems.length > 0) {
    turns.push(
      buildTurn({
        id: turnContext.userTurnId,
        role: 'user',
        branchId: turnContext.branchId,
        parentTurnId: turnContext.parentTurnId,
        itemIds: userItemIds,
        status: 'completed',
        createdAt: turnContext.createdAt,
        updatedAt: now,
      }),
    );
  }
  turns.push(
    buildTurn({
      id: turnContext.assistantTurnId,
      role: 'assistant',
      branchId: turnContext.branchId,
      parentTurnId: turnContext.userTurnId,
      itemIds: assistantItemIds,
      status: assistantStatus,
      createdAt: turnContext.createdAt,
      updatedAt: now,
    }),
  );
  await session.updateConversationGraph({
    activeBranchId: turnContext.branchId,
    turns,
    itemMeta: [
      ...userItems.flatMap((item) =>
        buildItemMeta(item, {
          turnId: turnContext.userTurnId,
          branchId: turnContext.branchId,
        }),
      ),
      ...assistantItems.flatMap((item) =>
        buildItemMeta(item, {
          turnId: turnContext.assistantTurnId,
          branchId: turnContext.branchId,
        }),
      ),
    ],
  });
}

export function getAssistantTurnStatus(
  state: RunState<any, any>,
): SessionTurnStatus {
  return state._currentStep?.type === 'next_step_interruption'
    ? 'interrupted'
    : 'completed';
}

function buildTurn(turn: SessionConversationTurn): SessionConversationTurn {
  return turn;
}

function buildItemMeta(
  item: AgentInputItem,
  options: { turnId: string; branchId: string },
): SessionConversationItemMeta[] {
  const itemId = getItemId(item);
  if (!itemId) {
    return [];
  }
  return [
    {
      itemId,
      turnId: options.turnId,
      branchId: options.branchId,
      origin: getItemOrigin(item),
    },
  ];
}

function collectItemIds(items: AgentInputItem[]): string[] {
  return items.flatMap((item) => {
    const id = getItemId(item);
    return id ? [id] : [];
  });
}

function getItemId(item: AgentInputItem): string | undefined {
  if (!item || typeof item !== 'object') {
    return undefined;
  }
  const id = (item as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

function getItemOrigin(item: AgentInputItem): SessionItemOrigin {
  if (!item || typeof item !== 'object') {
    return 'synthetic';
  }
  if ('role' in item) {
    switch (item.role) {
      case 'user':
        return 'user';
      case 'assistant':
        return 'assistant';
      case 'system':
        return 'system';
    }
  }
  const type = (item as { type?: unknown }).type;
  if (
    type === 'function_call' ||
    type === 'function_call_result' ||
    type === 'hosted_tool_call'
  ) {
    return 'tool';
  }
  if (type === 'reasoning') {
    return 'assistant';
  }
  return 'synthetic';
}

function findLatestTurnId(
  graph: SessionConversationGraphSnapshot,
  branchId: string,
): string | undefined {
  return Object.values(graph.turns)
    .filter((turn) => turn.branchId === branchId)
    .sort((a, b) => {
      const byUpdatedAt = a.updatedAt.localeCompare(b.updatedAt);
      if (byUpdatedAt !== 0) {
        return byUpdatedAt;
      }
      return a.createdAt.localeCompare(b.createdAt);
    })
    .at(-1)?.id;
}
